import { defineAsyncComponent, App } from 'vue';
import { blockResolver } from '@/core/app/componentResolver.service';

import BlockRender from './helpers/BlockRender.vue';
import BlockList from './helpers/BlockList.vue';
import TextBlock from './TextBlock.vue';

const HalfWidthBlocks = defineAsyncComponent(() => import('./HalfWidthBlocks.vue'));
const OnePlusTwoBlocks = defineAsyncComponent(() => import('./OnePlusTwoBlocks.vue'));
const HeroBannerBlock = defineAsyncComponent(() => import('./HeroBannerBlock.vue'));
const AdnuntiusHeroBannerBlock = defineAsyncComponent(() => import('./AdnuntiusHeroBannerBlock.vue'));
const UniqueSellingPointBlock = defineAsyncComponent(() => import('./UniqueSellingPointBlock.vue'));
const UniqueSellingPointItem = defineAsyncComponent(() => import('./UniqueSellingPointItem.vue'));
const BrandsBlock = defineAsyncComponent(() => import('./BrandsBlock.vue'));
const FullWidthTextBlock = defineAsyncComponent(() => import('./FullWidthTextBlock.vue'));
const TextWithImageBlock = defineAsyncComponent(() => import('./TextWithImageBlock.vue'));
const ProductRecommendationsBlock = defineAsyncComponent(() => import('./ProductRecommendationsBlock.vue'));
const BrandRecommendationsBlock = defineAsyncComponent(() => import('./BrandRecommendationsBlock.vue'));
const CategoryRecommendationsBlock = defineAsyncComponent(() => import('./CategoryRecommendationsBlock.vue'));
const ProductListBlock = defineAsyncComponent(() => import('./ProductListBlock.vue'));
const TabbedContentBlock = defineAsyncComponent(() => import('./TabbedContentBlock.vue'));
const QuoteBlock = defineAsyncComponent(() => import('./QuoteBlock.vue'));
const LatestNewsBlock = defineAsyncComponent(() => import('./LatestNewsBlock.vue'));
const LatestCampaignsBlock = defineAsyncComponent(() => import('./LatestCampaignsBlock.vue'));
const CategoryBlock = defineAsyncComponent(() => import('./categories/CategoryBlock.vue')); // Used on frontpage

// Lower priority - not in same chunk
const SimpleBannerBlock = defineAsyncComponent(() => import('./SimpleBannerBlock.vue'));
const EmployeeBlock = defineAsyncComponent(() => import('./information/EmployeeBlock.vue'));
const JotFormBlock = defineAsyncComponent(() => import('./JotFormBlock.vue'));
const CancelNewsletterBlock = defineAsyncComponent(() => import('./newsletter/CancelNewsletterBlock.vue'));
const SubscribeNewsletterBlock = defineAsyncComponent(() => import('./newsletter/SubscribeNewsletterBlock.vue'));
const ProductGuideBlock = defineAsyncComponent(() => import('./productguide/ProductGuideBlock.vue'));
const ProductGuideConfigurationBlock = defineAsyncComponent(() => import('./productguide/ProductGuideConfigurationBlock.vue'));
const IframeBlock = defineAsyncComponent(() => import('./IframeBlock.vue'));
const MapBlock = defineAsyncComponent(() => import('./google-maps/MapBlock.vue'));
const CableGuideBlock = defineAsyncComponent(() => import('./cableguide/CableGuideBlock.vue'));
const CompatabilityGuideBlock = defineAsyncComponent(() => import('@/project/app/blocks/CompatabilityGuide/CompatabilityGuideBlock.vue'));
const StatisticsBlock = defineAsyncComponent(() => import('./statistics/StatisticsBlock.vue'));
const CategoryTileBlock = defineAsyncComponent(() => import('./categories/CategoryTileBlock.vue'));
const CodeBlock = defineAsyncComponent(() => import('./CodeBlock.vue'));
const HeroBannerUniqueSellingPointsBlock = defineAsyncComponent(() => import('./HeroBannerUniqueSellingPointsBlock.vue'));
const SimpleUniqueSellingPointsBlock = defineAsyncComponent(() => import('./SimpleUniqueSellingPointsBlock.vue'));
const FaqBlock = defineAsyncComponent(() => import('./FaqBlock.vue'));
const StepsBlock = defineAsyncComponent(() => import('./StepsBlock.vue'));
const ProductSeriesBlock = defineAsyncComponent(() => import('./ProductSeriesBlock.vue'));
const ProductGuideBrandsBlock = defineAsyncComponent(() => import('./productguide/StepProductGuide/ProductGuideBrandsBlock.vue'));
const BrandProductGuideBlock = defineAsyncComponent(() => import('./productguide/StepProductGuide/BrandProductGuideBlock.vue'));
const ProductGuideSelectorBlock = defineAsyncComponent(() => import('./productguide/StepProductGuide/ProductGuideSelectorBlock.vue'));
const WebInquiryBlock = defineAsyncComponent(() => import('./webinquiries/WebInquiryBlock.vue'));
const VideoBlock = defineAsyncComponent(() => import('./VideoBlock.vue'));
const LegalContentBlock = defineAsyncComponent(() => import('./LegalContentBlock.vue'));
const DeveloperXmlValidationBlock = defineAsyncComponent(() => import('./DeveloperXmlValidationBlock.vue'));

const FallbackBlock = defineAsyncComponent(() => import('./FallbackBlock.vue'));

export type BlockName =
    'halfWidthBlocks' |
    'halfWidthBlock' |
    'twoPlusOneBlocks' |
    'heroBanner' |
    'heroBannerWithUsp' |
    'personalizedContent' |
    'uniqueSellingPointBlock' |
    'brandsBlock' |
    'codeBlock' |
    'uniqueSellingPointItem' |
    'textBlock' |
    'fullWidthTextBlock' |
    'categoryBlock' |
    'jotFormBlock' |
    'cancelNewsletterBlock' |
    'subscribeNewsletterBlock' |
    'textWithImageBlock' |
    'productGuideBlock' |
    'productGuideConfigurationBlock' |
    'cableGuideBlock' |
    'compatibilityBlock' |
    'iframeBlock' |
    'productRecommendationsBlock' |
    'categoryRecommendationsBlock' |
    'productListBlock' |
    'tabbedContentBlock' |
    'quoteBlock' |
    'map' |
    'latestNewsBlock' |
    'latestCampaignsBlock' |
    'categoryTileBlock' |
    'statisticsBlock' |
    'employeesBlock' |
    'brandRecommendationsBlock' |
    'faqBlock' |
    'stepsBlock' |
    'simpleUspBlock' |
    'productSeriesBlock' |
    'simpleBannerBlock' |
    'productGuideBrandsBlock' |
    'productGuideSelectorBlock' |
    'brandProductGuideBlock' |
    'webInquiryBlock' |
    'videoBlock' |
    'legalContentBlock' |
    'developmentXmlValidation';

blockResolver.register<BlockName>('halfWidthBlocks', HalfWidthBlocks);
blockResolver.register<BlockName>('twoPlusOneBlocks', OnePlusTwoBlocks);
blockResolver.register<BlockName>('heroBanner', HeroBannerBlock);
blockResolver.register<BlockName>('heroBannerWithUsp', HeroBannerUniqueSellingPointsBlock);
blockResolver.register<BlockName>('simpleUspBlock', SimpleUniqueSellingPointsBlock);
blockResolver.register<BlockName>('personalizedContent', AdnuntiusHeroBannerBlock);
blockResolver.register<BlockName>('uniqueSellingPointBlock', UniqueSellingPointBlock);
blockResolver.register<BlockName>('brandsBlock', BrandsBlock);
blockResolver.register<BlockName>('uniqueSellingPointItem', UniqueSellingPointItem);
blockResolver.register<BlockName>('textBlock', TextBlock);
blockResolver.register<BlockName>('codeBlock', CodeBlock);
blockResolver.register<BlockName>('fullWidthTextBlock', FullWidthTextBlock);
blockResolver.register<BlockName>('categoryBlock', CategoryBlock);
blockResolver.register<BlockName>('jotFormBlock', JotFormBlock);
blockResolver.register<BlockName>('cancelNewsletterBlock', CancelNewsletterBlock);
blockResolver.register<BlockName>('subscribeNewsletterBlock', SubscribeNewsletterBlock);
blockResolver.register<BlockName>('textWithImageBlock', TextWithImageBlock);
blockResolver.register<BlockName>('productGuideBlock', ProductGuideBlock);
blockResolver.register<BlockName>('productGuideConfigurationBlock', ProductGuideConfigurationBlock);
blockResolver.register<BlockName>('cableGuideBlock', CableGuideBlock);
blockResolver.register<BlockName>('compatibilityBlock', CompatabilityGuideBlock);
blockResolver.register<BlockName>('iframeBlock', IframeBlock);
blockResolver.register<BlockName>('productRecommendationsBlock', ProductRecommendationsBlock);
blockResolver.register<BlockName>('categoryRecommendationsBlock', CategoryRecommendationsBlock);
blockResolver.register<BlockName>('productListBlock', ProductListBlock);
blockResolver.register<BlockName>('tabbedContentBlock', TabbedContentBlock);
blockResolver.register<BlockName>('quoteBlock', QuoteBlock);
blockResolver.register<BlockName>('map', MapBlock);
blockResolver.register<BlockName>('latestNewsBlock', LatestNewsBlock);
blockResolver.register<BlockName>('latestCampaignsBlock', LatestCampaignsBlock);
blockResolver.register<BlockName>('categoryTileBlock', CategoryTileBlock);
blockResolver.register<BlockName>('statisticsBlock', StatisticsBlock);
blockResolver.register<BlockName>('employeesBlock', EmployeeBlock);
blockResolver.register<BlockName>('brandRecommendationsBlock', BrandRecommendationsBlock);
blockResolver.register<BlockName>('faqBlock', FaqBlock);
blockResolver.register<BlockName>('stepsBlock', StepsBlock);
blockResolver.register<BlockName>('productSeriesBlock', ProductSeriesBlock);
blockResolver.register<BlockName>('productGuideBrandsBlock', ProductGuideBrandsBlock);
blockResolver.register<BlockName>('brandProductGuideBlock', BrandProductGuideBlock);
blockResolver.register<BlockName>('productGuideSelectorBlock', ProductGuideSelectorBlock);
blockResolver.register<BlockName>('simpleBannerBlock', SimpleBannerBlock);
blockResolver.register<BlockName>('webInquiryBlock', WebInquiryBlock);
blockResolver.register<BlockName>('videoBlock', VideoBlock);
blockResolver.register<BlockName>('legalContentBlock', LegalContentBlock);
blockResolver.register<BlockName>('developmentXmlValidation', DeveloperXmlValidationBlock);

blockResolver.registerFallback(FallbackBlock);

export default function config(app: App) {
    app.component('BlockRender', BlockRender);
    app.component('BlockList', BlockList);
}
